import { format, isValid, parseISO } from 'date-fns';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { ArrowUp, FlexRow, Header, Icon, Text } from '@summer/ui-components';

import Badge from 'components/Badge';
import { Box } from 'components/box/Box';

const DEFAULT_INVITE_LIMIT = 3;

type HhsUser = {
  id: number;
  email: string;
};

type HhsInvite = {
  uuid: string;
  inviteeName: string;
  invitationExpiresAt: string;
  createdAt: string;
  accepted: boolean;
  hhsUser: HhsUser | null;
};

export type HouseholdSharingData = {
  hhsEnabled: boolean;
  hhsInvites: number | null;
  invites: HhsInvite[];
};

type HouseholdSharingProps = {
  householdSharing?: HouseholdSharingData;
};

type InviteStatus = 'accepted' | 'pending' | 'expired';

type DecoratedInvite = {
  invite: HhsInvite;
  status: InviteStatus;
};

const STATUS_LABEL: Record<InviteStatus, string> = {
  accepted: 'Accepted',
  pending: 'Pending',
  expired: 'Expired',
};

const STATUS_VARIANT: Record<InviteStatus, 'success' | 'neutral' | 'error'> = {
  accepted: 'success',
  pending: 'neutral',
  expired: 'error',
};

const getStatus = (invite: HhsInvite, now: Date): InviteStatus => {
  if (invite.accepted && invite.hhsUser) return 'accepted';
  const expiresAt = parseISO(invite.invitationExpiresAt);
  if (isValid(expiresAt) && expiresAt.getTime() < now.getTime()) {
    return 'expired';
  }
  return 'pending';
};

const formatDate = (isoDate: string): string => {
  const date = parseISO(isoDate);
  if (!isValid(date)) return 'N/A';
  return format(date, 'MMM d, yyyy');
};

const renderInviteBody = (
  decoratedInvites: DecoratedInvite[],
  hhsEnabled: boolean,
  hasData: boolean,
) => {
  if (!hasData) {
    return null;
  }
  if (!hhsEnabled) {
    return (
      <Text color="secondary">
        Household sharing is not enabled for this partner.
      </Text>
    );
  }
  if (decoratedInvites.length === 0) {
    return <Text color="secondary">No invites have been sent yet.</Text>;
  }
  return (
    <List>
      {decoratedInvites.map(({ invite, status }) => (
        <ListItem key={invite.uuid}>
          <ItemHeader>
            <ItemPrimary>
              {status === 'accepted' && invite.hhsUser ? (
                <NameLink to={`/borrowers/${String(invite.hhsUser.id)}`}>
                  {invite.inviteeName}
                </NameLink>
              ) : (
                <InviteeName>{invite.inviteeName}</InviteeName>
              )}
            </ItemPrimary>
            <Badge variant={STATUS_VARIANT[status]}>
              {STATUS_LABEL[status]}
            </Badge>
          </ItemHeader>
          {status !== 'accepted' && (
            <ItemMeta>
              {status === 'expired' ? 'Expired' : 'Expires'} on{' '}
              {formatDate(invite.invitationExpiresAt)}
            </ItemMeta>
          )}
        </ListItem>
      ))}
    </List>
  );
};

const HouseholdSharing = ({ householdSharing }: HouseholdSharingProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasData = householdSharing !== undefined;
  const hhsEnabled = householdSharing?.hhsEnabled ?? false;
  const inviteLimit = householdSharing?.hhsInvites ?? DEFAULT_INVITE_LIMIT;
  const invites = householdSharing?.invites ?? [];
  const now = new Date();

  const decoratedInvites: DecoratedInvite[] = invites.map(invite => ({
    invite,
    status: getStatus(invite, now),
  }));

  const acceptedCount = decoratedInvites.filter(
    ({ status }) => status === 'accepted',
  ).length;

  const toggle = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <Box mt={40}>
      <CollapseTrigger
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        {/* @ts-expect-error size overrides the computed font-size and weight is inert — see [sc-52767] */}
        <Header size="large" weight="bold">
          Household Sharing
        </Header>
        <Arrow fill="#6D7581" $open={isOpen} SvgComponent={ArrowUp} />
        {hhsEnabled && (
          <UsageContainer>
            <Text size="small">
              Used {acceptedCount} of {inviteLimit} invites
            </Text>
          </UsageContainer>
        )}
      </CollapseTrigger>

      {isOpen && (
        <Body
          bg="white"
          border="solid 1px"
          borderRadius={6}
          borderColor="blueGrey"
          px={24}
          pt={24}
          pb={24}
          mt={24}
        >
          {renderInviteBody(decoratedInvites, hhsEnabled, hasData)}
        </Body>
      )}
    </Box>
  );
};

const CollapseTrigger = styled(FlexRow)`
  width: 100%;
  cursor: pointer;
  padding: 16px 0;
  align-items: center;
`;

const Arrow = styled(Icon)<{ $open: boolean }>`
  cursor: pointer;
  margin-left: 8px;
  transform: ${props => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease-out;
`;

const UsageContainer = styled.div`
  margin-left: 16px;
  align-self: center;
`;

const Body = styled(Box)`
  min-height: 80px;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  padding: 14px 0;
  & + & {
    border-top: 1px solid #e6eaf0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ItemPrimary = styled.div`
  flex: 1;
  min-width: 0;
`;

const InviteeName = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #2d3340;
`;

const NameLink = styled(Link)`
  color: #1565c0;
  font-weight: 600;
  text-decoration: none;
  font-size: 16px;

  &:hover {
    text-decoration: underline;
  }
`;

const ItemMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6d7581;
`;

export default HouseholdSharing;
